/**
 * Strategi → Præsentationer: registret over emner (typer) og dæk, delt af
 * server og web ([D-534], Charlie 03-11-2026).
 *
 * Kuraterede dæk: hvert dæk er en Vue-komponent med LIVE tal fra API'et, så
 * et dæk kan genbruges måned for måned ved at tilføje en række her (ingen
 * kopiering af slides).
 *
 * ADGANG ([D-534]): Charlie: "tildele adgang til enten alle præsentationer,
 * enkelte præsentationstyper (fx ledelsesmøder, kampagner, bureaumøder) eller
 * enkelte præsentationer (fx Black November 2026, performancemøde med
 * Morningtrain)". Hver type og hvert dæk er sin egen katalogside
 * (`presentationTopicPath`, `presentationDeckPath`), og adgangen ARVES NEDAD
 * kun i denne gren: den effektive adgang til et dæk er den højeste af dækket,
 * typen og Alle (`presentationAccess`). Der findes ingen fratagelse. Har man
 * kun adgang til en type eller et dæk, ser man menupunktet og en oversigt med
 * netop det, man har adgang til (Charlie 03-11).
 *
 * Månedsdæk er nøglet på MÅNEDEN ('2026-10' = oktober-præsentationen):
 * akt 2 viser nøglemåneden, akt 1 den afsluttede måned før. Udgivelsen
 * styres af serveren (presentation_releases): auto den 26. i måneden,
 * eller gennemtvunget tidligere af en bruger med edit-ret. Et månedsdæk er 1
 * dæk pr. måned: adgang til oktober giver ikke november; løbende adgang er
 * typen Ledelsesmøder.
 *
 * Ugedæk (Charlie 01-11-2026, [D-510]) er nøglet på kampagnens slug og bærer
 * kampagnens public_id og `weekly`: få slides med kampagnens ugetal (hele dage
 * til og med i går), hver uge mod ugen før, sidste år og prognosens midt
 * (målet). 2 møder, 2 dæk, som ikke blandes: ledelsen (Dennis og Benjamin,
 * emnet Kampagner) og Morningtrain (emnet Bureaumøder, følger mødets agenda;
 * Charlies input og beslutningerne skrives på dækket). De har ingen
 * udgivelses-livscyklus. Et månedsdæk kan stadig lægge en kampagnes
 * plan-slides sidst (appendCampaignId); dér er de kun plan.
 */

export interface PresentationTopic {
  slug: string;
  title: string;
  description: string;
}

export interface PresentationEntry {
  /** URL-id under emnet: månedsnøglen 'YYYY-MM' (månedsdæk) eller kampagnens slug. */
  id: string;
  topic: string;
  title: string;
  /** Præsentationens måned ('YYYY-MM'): akt 2 = denne, akt 1 = måneden før. */
  month: string;
  /** Ugedæk: kampagnens public_id. */
  campaignId?: string;
  /** Ugedæk: hvilket møde (slides og tal følger mødet). */
  weekly?: 'ledelse' | 'morningtrain';
  /** Ugedæk: mødets ugedag (1 = mandag, ugerne man-søn; 2 = tirsdag, ugerne tir-man). */
  weekStart?: 1 | 2;
  /** Ugedæk: hvem mødet er med (titelslidet). */
  audience?: string;
  /** Månedsdæk: en kampagne hvis slides lægges sidst i dækket (før Drøftelse). */
  appendCampaignId?: string;
}

/** Kampagnen Black November 2026 (fast public_id, [D-304]). */
const BLACK_NOVEMBER_2026_ID = '50835c1ce913aee43b2b7d881ee4d98a';

export const PRESENTATION_TOPICS: PresentationTopic[] = [
  {
    slug: 'management',
    title: 'Ledelsesmøder',
    description:
      'Månedsmødets fælles overblik: de store linjer for måneden, hvert tal målt mod måneden før (MTM) og samme måned året før (YoY).',
  },
  {
    slug: 'campaigns',
    title: 'Kampagner',
    description:
      'Kampagnens ugestatus til ledelsen: omsætning og ordreindgang, butikken, bruttofortjeneste og annoncekroner, uge for uge mod målet og sidste år.',
  },
  {
    slug: 'agency',
    title: 'Bureaumøder',
    description:
      'Ugentlige performancemøder med bureauet: tallene siden sidst mod ugen før, sidste år og målet, hvad der flytter sig, og beslutningerne med ejer og deadline.',
  },
];

export const PRESENTATIONS: PresentationEntry[] = [
  {
    id: '2026-10',
    topic: 'management',
    title: 'Månedsmøde med ledelsen, okt 2026',
    month: '2026-10',
    // Charlie 27-10-2026: Black November gennemgås kort på de sidste slides.
    appendCampaignId: BLACK_NOVEMBER_2026_ID,
  },
  {
    id: 'black-november-2026-status',
    topic: 'campaigns',
    title: 'Black November 2026, ugestatus til ledelsen',
    month: '2026-11',
    campaignId: BLACK_NOVEMBER_2026_ID,
    weekly: 'ledelse',
    weekStart: 1,
    audience: 'Dennis, Benjamin og Charlie',
  },
  {
    id: 'black-november-2026-morningtrain',
    topic: 'agency',
    title: 'Black November 2026, performancemøde med Morningtrain',
    month: '2026-11',
    campaignId: BLACK_NOVEMBER_2026_ID,
    weekly: 'morningtrain',
    weekStart: 2,
    audience: 'Morningtrain og Bad & Fliser',
  },
];

export function topicBySlug(slug: string): PresentationTopic | null {
  return PRESENTATION_TOPICS.find((t) => t.slug === slug) ?? null;
}

export function presentationsForTopic(slug: string): PresentationEntry[] {
  return PRESENTATIONS.filter((p) => p.topic === slug).sort((a, b) => b.month.localeCompare(a.month));
}

export function presentationById(topic: string, id: string): PresentationEntry | null {
  return PRESENTATIONS.find((p) => p.topic === topic && p.id === id) ?? null;
}

/** Måneden før 'YYYY-MM'. */
export function prevMonthOf(ym: string): string {
  const y = Number(ym.slice(0, 4));
  const m = Number(ym.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** Akt 1's måned: den afsluttede måned før præsentationens måned. */
export function dataMonthOf(entry: PresentationEntry): string {
  return prevMonthOf(entry.month);
}

/** Kampagnedæk har ingen udgivelses-livscyklus (kun månedsdæk har). */
export function hasReleaseCycle(entry: PresentationEntry): boolean {
  return entry.campaignId == null;
}

/** Udgivelses-nøglen i presentation_releases. */
export function releaseKeyOf(entry: PresentationEntry): string {
  return `${entry.topic}:${entry.month}`;
}

// ── Adgang ([D-534]) ─────────────────────────────────────────────────────────

/** Alle præsentationer: oversigten og det øverste grant i grenen. */
export const PRESENTATIONS_PATH = '/strategy/presentations';

/** Katalogsiden for en type (fx '/strategy/presentations/agency'). */
export function presentationTopicPath(slug: string): string {
  return `${PRESENTATIONS_PATH}/${slug}`;
}

/** Katalogsiden for et dæk (fx '/strategy/presentations/agency/black-november-2026-morningtrain'). */
export function presentationDeckPath(entry: Pick<PresentationEntry, 'topic' | 'id'>): string {
  return `${PRESENTATIONS_PATH}/${entry.topic}/${entry.id}`;
}

/** Dæk-nøglen, som API-kaldene bærer ('agency:black-november-2026-morningtrain'). */
export function presentationDeckKey(entry: Pick<PresentationEntry, 'topic' | 'id'>): string {
  return `${entry.topic}:${entry.id}`;
}

export function presentationByKey(key: string): PresentationEntry | null {
  const i = key.indexOf(':');
  if (i <= 0) return null;
  return presentationById(key.slice(0, i), key.slice(i + 1));
}

/**
 * Forælderen i adgangstræet: dæk → type → Alle. null for Alle selv og for
 * sider uden for grenen. Bruges af Siderettigheder og Brugere til at vise
 * træet og arvet adgang.
 */
export function presentationParentPath(path: string): string | null {
  if (!path.startsWith(`${PRESENTATIONS_PATH}/`)) return null;
  const [topicSlug, deckId] = path.slice(PRESENTATIONS_PATH.length + 1).split('/');
  if (!topicSlug || !topicBySlug(topicSlug)) return null;
  if (!deckId) return PRESENTATIONS_PATH;
  return presentationById(topicSlug, deckId) ? presentationTopicPath(topicSlug) : null;
}

type Level = 'view' | 'edit';

function maxLevel(levels: Array<Level | null | undefined>): Level | undefined {
  if (levels.includes('edit')) return 'edit';
  if (levels.includes('view')) return 'view';
  return undefined;
}

/**
 * Den effektive adgang til en sti i præsentationsgrenen, givet brugerens
 * grants pr. katalogside. `undefined` = ingen adgang; `null` = stien er ikke
 * i grenen (brug den almindelige regel).
 *   - Et dæk (også med slide-nummer bagved): den højeste af dækket, typen og Alle.
 *   - En type: den højeste af typen og Alle; ellers view, hvis man har et dæk
 *     i typen (så typens liste kan vise det).
 *   - Oversigten: Alle; ellers view, hvis man har en type eller et dæk.
 * Ukendte typer og dæk falder tilbage på Alle.
 */
export function presentationAccess(path: string, grantOf: (pagePath: string) => Level | null | undefined): Level | undefined | null {
  if (path !== PRESENTATIONS_PATH && !path.startsWith(`${PRESENTATIONS_PATH}/`)) return null;
  const all = grantOf(PRESENTATIONS_PATH) ?? undefined;
  const [topicSlug, deckId] = path.slice(PRESENTATIONS_PATH.length + 1).split('/');
  const anyDeck = (decks: PresentationEntry[]): boolean => decks.some((d) => grantOf(presentationDeckPath(d)) != null);
  if (!topicSlug) {
    if (all) return all;
    const anyTopic = PRESENTATION_TOPICS.some((t) => grantOf(presentationTopicPath(t.slug)) != null);
    return anyTopic || anyDeck(PRESENTATIONS) ? 'view' : undefined;
  }
  const topic = topicBySlug(topicSlug);
  if (!topic) return all;
  const topicLevel = maxLevel([all, grantOf(presentationTopicPath(topic.slug))]);
  if (!deckId) return topicLevel ?? (anyDeck(presentationsForTopic(topic.slug)) ? 'view' : undefined);
  const deck = presentationById(topic.slug, deckId);
  if (!deck) return topicLevel;
  return maxLevel([topicLevel, grantOf(presentationDeckPath(deck))]);
}
